import { useMemo } from 'react';

import { DateRange } from 'react-day-picker';

import { StatsResult, TrendsResult } from 'api/useAnalytics';
import Decimal from 'decimal.js';
import keyBy from 'lodash.keyby';
import {
  BaseStatisticValue,
  EStatisticType
} from 'views/Statistics/StatisticsView.types';

import { isDateStringHourly } from './utils';

interface UseAnalyticsChartDataParams {
  stats?: StatsResult[];
  trends?: TrendsResult[];
  previousStats?: StatsResult[];
  previousTrends?: TrendsResult[];
  previousDateRange?: DateRange;
  currentDateRange?: DateRange;
}

export interface AnalyticsChartData {
  name: string;
  type: EStatisticType;
  stat: StatsResult;
  currentTrends: BaseStatisticValue[];
  previousTrends?: BaseStatisticValue[];
}

interface Dictionary<T> {
  [index: string]: T;
}

export function useAnalyticsChartData({
  stats,
  trends,
  previousStats,
  previousTrends,
  previousDateRange,
  currentDateRange
}: UseAnalyticsChartDataParams): Dictionary<AnalyticsChartData> {
  return useMemo(() => {
    const statsByMetric = keyBy(stats, 'name');
    const trendsByMetric = keyBy(trends, 'name');
    const previousStatsByMetric = keyBy(previousStats, 'name');
    const previousTrendsByMetric = keyBy(previousTrends, 'name');

    const result = Object.keys(statsByMetric).map((key) => {
      const currentTrends = formatTrendDates(
        trendsByMetric[key]?.values ?? [],
        currentDateRange
      );
      const previousTrends = formatTrendDates(
        previousTrendsByMetric[key]?.values,
        previousDateRange
      );

      return {
        name: key,
        type: statsByMetric[key]?.type,
        stat: formatStat(statsByMetric[key], previousStatsByMetric[key]),
        currentTrends,
        previousTrends
      };
    });

    return keyBy(result, 'name');
  }, [
    stats,
    trends,
    previousStats,
    previousTrends,
    currentDateRange,
    previousDateRange
  ]);
}

function formatTrendDates(
  trends?: BaseStatisticValue[],
  dateRange?: DateRange
): BaseStatisticValue[] {
  if (!trends || !dateRange?.from) return trends || [];

  return trends.map((trend) => {
    if (trend.date && isDateStringHourly(trend.date)) {
      const hour = trend.date.padStart(2, '0');
      const baseDate = new Date(dateRange.from as Date);

      baseDate.setHours(Number(hour), 0, 0, 0);

      return {
        ...trend,
        date: baseDate.toISOString()
      };
    }

    return trend;
  });
}

function formatStat(
  stat: StatsResult,
  previousStat?: StatsResult
): StatsResult {
  return {
    name: stat.name,
    type: stat.type,
    currentValue: stat.currentValue,
    previousValue: previousStat?.currentValue,
    changePercentage: calculateChangePercentage(
      stat.currentValue ?? 0,
      previousStat?.currentValue
    )
  };
}

function calculateChangePercentage(
  currentValue: number,
  previousValue?: number
): number | undefined {
  if (previousValue === undefined) {
    return undefined;
  }

  if (Number(previousValue) === 0) {
    const absolutePercentageChange = Number(currentValue) > 0 ? 100 : -100;
    return Number(currentValue) === 0 ? 0 : absolutePercentageChange;
  }

  const result = new Decimal(currentValue)
    .sub(previousValue)
    .div(previousValue);
  const percentage = result.mul(100);
  return Number(percentage.toFixed(2));
}
